import { useState, useEffect, useCallback, type FormEvent } from 'react';
import { Loader2, X } from 'lucide-react';
import { useStoreMe, useEffectiveStoreNo } from '@/lib/useStores';
import { todayInStoreZone } from '@/lib/useToday';
import { useItemSearch, lookupBarcodeItem, type PulseItem } from '@/lib/usePulse';
import { BarcodeScanner, type BarcodeScanResult } from '@/components/BarcodeScanner';
import { reportScannerEvent } from '@/lib/reportScannerEvent';
import {
  useSampling,
  useAddSamplingEntry,
  useDeleteSamplingEntry,
  type SamplingEntry,
} from '@/lib/useSampling';

/**
 * Sampling slots, as minutes past midnight.
 *
 * Half-hour steps from 10:00 to 21:30. Stores sample on the half hour as well
 * as the hour, and with whole-hour slots a 10:30 round had nowhere to go: it
 * either overwrote the 10:00 entry for the same item or ended up in the notes.
 *
 * One number per slot rather than an (hour, minute) pair keeps the grid, the
 * selection state and the grouping trivial. It splits back into the hour and
 * minute the API stores at the one point that talks to the API.
 */
const SLOT_STEP_MINUTES = 30;
const FIRST_SLOT_MINUTES = 10 * 60;
const LAST_SLOT_MINUTES = 21 * 60 + 30;
const SLOTS = Array.from(
  { length: (LAST_SLOT_MINUTES - FIRST_SLOT_MINUTES) / SLOT_STEP_MINUTES + 1 },
  (_, i) => FIRST_SLOT_MINUTES + i * SLOT_STEP_MINUTES,
);

/** Split a slot into the hour and minute the API stores. */
function slotParts(slot: number): { hour: number; minute: number } {
  return { hour: Math.floor(slot / 60), minute: slot % 60 };
}

/** Rebuild a slot from a stored entry. */
function entrySlot(e: SamplingEntry): number {
  return e.hour_slot * 60 + (e.minute_slot ?? 0);
}

/** Compact label for a chip: `10a`, `10:30a`, `1p`, `1:30p`. */
function formatSlotShort(slot: number): string {
  const { hour, minute } = slotParts(slot);
  const suffix = hour < 12 ? 'a' : 'p';
  const h = hour % 12 === 0 ? 12 : hour % 12;
  return minute === 0 ? `${h}${suffix}` : `${h}:30${suffix}`;
}

/** Full label for the panel header: `10:30 AM`. */
function formatSlotLong(slot: number): string {
  const { hour, minute } = slotParts(slot);
  const suffix = hour < 12 ? 'AM' : 'PM';
  const h = hour % 12 === 0 ? 12 : hour % 12;
  return `${h}:${String(minute).padStart(2, '0')} ${suffix}`;
}

function useDebounce(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

const inputCls =
  'w-full rounded-md border border-candy-line bg-candy-white px-3 py-2 text-[14px] ' +
  'text-candy-ink placeholder:text-candy-ink-muted focus:border-candy-pink focus:outline-none ' +
  'dark:border-candy-dark-line dark:bg-candy-dark-surface dark:text-candy-dark-ink ' +
  'dark:placeholder:text-candy-dark-ink-muted dark:focus:border-candy-pink-dark';

// --- Slot chip ---

function SlotChip({
  slot,
  entries,
  selected,
  onClick,
}: {
  slot: number;
  entries: SamplingEntry[];
  selected: boolean;
  onClick: () => void;
}) {
  const filled = entries.length > 0;
  const sublabel =
    entries.length === 1
      ? (entries[0]?.item_descr ?? entries[0]?.item_no ?? '')
      : entries.length > 1
        ? `${entries.length} items`
        : '';

  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'flex w-full flex-col items-center justify-center rounded-xl border px-2 py-3 text-center transition-colors',
        selected
          ? 'border-candy-pink ring-2 ring-candy-pink/30 dark:border-candy-pink-dark dark:ring-candy-pink-dark/30'
          : filled
            ? 'border-candy-pink/30 bg-candy-pink/10 dark:border-candy-pink-dark/30 dark:bg-candy-pink-dark/10'
            : 'border-candy-line bg-candy-white hover:bg-candy-cream-warm dark:border-candy-dark-line dark:bg-candy-dark-surface dark:hover:bg-candy-dark-surface-warm',
      ].join(' ')}
    >
      <span
        className={`text-[13px] font-semibold ${
          filled
            ? 'text-candy-pink dark:text-candy-pink-dark'
            : 'text-candy-ink dark:text-candy-dark-ink'
        }`}
      >
        {formatSlotShort(slot)}
      </span>
      {sublabel && (
        <span className="mt-0.5 line-clamp-1 max-w-full text-[11px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
          {sublabel}
        </span>
      )}
    </button>
  );
}

// --- Entry row with inline delete confirm ---

function EntryRow({ entry, storeNo }: { entry: SamplingEntry; storeNo?: number }) {
  const [confirming, setConfirming] = useState(false);
  const deleteMut = useDeleteSamplingEntry(storeNo);
  const name = entry.item_descr ?? entry.item_no ?? 'Unknown item';

  return (
    <div className="flex items-start gap-3 border-b border-candy-line py-2.5 last:border-0 dark:border-candy-dark-line">
      <div className="min-w-0 flex-1">
        <p className="truncate text-[14px] font-medium text-candy-ink dark:text-candy-dark-ink">
          {name}
        </p>
        {entry.item_no && entry.item_descr && (
          <p className="text-[12px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
            #{entry.item_no}
          </p>
        )}
        {entry.notes && (
          <p className="text-[12px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
            {entry.notes}
          </p>
        )}
      </div>
      {confirming ? (
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={() => deleteMut.mutate(entry.id, { onSuccess: () => setConfirming(false) })}
            disabled={deleteMut.isPending}
            className="rounded px-2 py-0.5 text-[12px] font-semibold text-red-600 hover:bg-red-50 disabled:opacity-60 dark:text-red-400 dark:hover:bg-red-900/20"
          >
            Delete
          </button>
          <button
            type="button"
            onClick={() => setConfirming(false)}
            className="rounded px-2 py-0.5 text-[12px] text-candy-ink-muted hover:bg-candy-cream-warm dark:text-candy-dark-ink-muted dark:hover:bg-candy-dark-surface-warm"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          aria-label="Delete entry"
          className="mt-0.5 shrink-0 rounded p-1 text-candy-ink-muted hover:bg-candy-cream-warm hover:text-candy-pink dark:text-candy-dark-ink-muted dark:hover:bg-candy-dark-surface-warm dark:hover:text-candy-pink-dark"
        >
          <X size={14} aria-hidden />
        </button>
      )}
    </div>
  );
}

// --- Add form ---

function AddForm({
  slot,
  date,
  storeNo,
  onSuccess,
}: {
  slot: number;
  date: string;
  storeNo?: number;
  onSuccess: () => void;
}) {
  const [mode, setMode] = useState<'search' | 'freetext'>('search');
  const [searchText, setSearchText] = useState('');
  const [selectedItem, setSelectedItem] = useState<PulseItem | null>(null);
  const [freeText, setFreeText] = useState('');
  const [note, setNote] = useState('');
  const [scanLoading, setScanLoading] = useState(false);
  const [scanNoMatch, setScanNoMatch] = useState(false);

  const q = useDebounce(searchText, 300);
  const searchQ = useItemSearch(q, storeNo);
  const addMut = useAddSamplingEntry(storeNo);

  const canSubmit = mode === 'search' ? selectedItem !== null : freeText.trim().length > 0;

  const handleBarcodeFound = useCallback(async (code: string, scan: BarcodeScanResult) => {
    setScanLoading(true);
    setScanNoMatch(false);
    const startedAt = Date.now();
    try {
      const item = await lookupBarcodeItem(code, storeNo);
      reportScannerEvent({
        type: item ? 'lookup_found' : 'lookup_not_found',
        source: scan.source,
        storeNo,
        barcodeLength: code.length,
        durationMs: Date.now() - startedAt,
      });
      if (item) {
        setSelectedItem(item);
      } else {
        setScanNoMatch(true);
      }
    } catch {
      reportScannerEvent({
        type: 'lookup_failed',
        source: scan.source,
        storeNo,
        barcodeLength: code.length,
        durationMs: Date.now() - startedAt,
      });
      setScanNoMatch(true);
    } finally {
      setScanLoading(false);
    }
  }, [storeNo]);

  function handleModeSwitch(m: 'search' | 'freetext') {
    setMode(m);
    setSearchText('');
    setSelectedItem(null);
    setFreeText('');
    setScanNoMatch(false);
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    const { hour, minute } = slotParts(slot);
    const payload: {
      log_date: string;
      hour_slot: number;
      minute_slot: number;
      item_no?: string;
      item_descr?: string;
      notes?: string;
    } = { log_date: date, hour_slot: hour, minute_slot: minute };
    if (mode === 'search' && selectedItem) {
      payload.item_no = selectedItem.item_no;
    } else {
      payload.item_descr = freeText.trim();
    }
    if (note.trim()) payload.notes = note.trim();
    addMut.mutate(payload, {
      onSuccess: () => {
        setSearchText('');
        setSelectedItem(null);
        setFreeText('');
        setNote('');
        onSuccess();
      },
    });
  }

  const showResults =
    q.length >= 2 && !selectedItem && !searchQ.isLoading && (searchQ.data?.length ?? 0) > 0;
  const showNoResults =
    q.length >= 2 && !selectedItem && !searchQ.isLoading && (searchQ.data?.length ?? 0) === 0;

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {/* Mode toggle */}
      <div className="flex overflow-hidden rounded-md border border-candy-line dark:border-candy-dark-line">
        {(['search', 'freetext'] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => handleModeSwitch(m)}
            className={`flex-1 px-3 py-1.5 text-[13px] font-medium transition-colors ${
              mode === m
                ? 'bg-candy-pink text-white dark:bg-candy-pink-dark dark:text-candy-dark-bg'
                : 'bg-candy-white text-candy-ink-muted hover:bg-candy-cream-warm dark:bg-candy-dark-surface dark:text-candy-dark-ink-muted dark:hover:bg-candy-dark-surface-warm'
            }`}
          >
            {m === 'search' ? 'Item search' : 'Free text'}
          </button>
        ))}
      </div>

      {/* Item input */}
      {mode === 'search' ? (
        selectedItem ? (
          <div className="flex items-center justify-between gap-3 rounded-md border border-candy-line bg-candy-cream-warm px-3 py-2 dark:border-candy-dark-line dark:bg-candy-dark-surface-warm">
            <div className="min-w-0">
              <p className="truncate text-[14px] font-medium text-candy-ink dark:text-candy-dark-ink">
                {selectedItem.descr}
              </p>
              <p className="text-[12px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
                #{selectedItem.item_no}
              </p>
            </div>
            <button
              type="button"
              onClick={() => { setSelectedItem(null); setScanNoMatch(false); }}
              className="shrink-0 text-[12px] text-candy-ink-muted underline-offset-2 hover:text-candy-pink hover:underline dark:text-candy-dark-ink-muted dark:hover:text-candy-pink-dark"
            >
              Change
            </button>
          </div>
        ) : (
          <>
            <BarcodeScanner onBarcodeFound={handleBarcodeFound} storeNo={storeNo} />
            {scanLoading && (
              <p className="mb-2 flex items-center gap-1.5 text-[12px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
                <Loader2 size={12} className="animate-spin" aria-hidden /> Looking up barcode…
              </p>
            )}
            {scanNoMatch && (
              <p className="mb-2 text-[12px] text-candy-pink dark:text-candy-pink-dark">
                No item found for this barcode.
              </p>
            )}
            <div className="relative">
              <input
                type="search"
                value={searchText}
                onChange={(e) => { setSearchText(e.target.value); setScanNoMatch(false); }}
                placeholder="Search by name or item #…"
                className={inputCls}
                autoComplete="off"
              />
              {searchQ.isLoading && (
                <Loader2
                  size={14}
                  className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-candy-ink-muted dark:text-candy-dark-ink-muted"
                  aria-hidden
                />
              )}
              {showResults && (
                <ul className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-candy-line bg-candy-white shadow-1 dark:border-candy-dark-line dark:bg-candy-dark-surface">
                  {searchQ.data!.map((item) => (
                    <li key={item.item_no}>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedItem(item);
                          setSearchText('');
                          setScanNoMatch(false);
                        }}
                        className="flex w-full flex-col px-3 py-2 text-left hover:bg-candy-cream-warm dark:hover:bg-candy-dark-surface-warm"
                      >
                        <span className="text-[14px] font-medium text-candy-ink dark:text-candy-dark-ink">
                          {item.descr}
                        </span>
                        <span className="text-[12px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
                          #{item.item_no}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {showNoResults && (
                <p className="mt-1 text-[12px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
                  No items found for &ldquo;{q}&rdquo;
                </p>
              )}
            </div>
          </>
        )
      ) : (
        <input
          type="text"
          value={freeText}
          onChange={(e) => setFreeText(e.target.value)}
          placeholder="Item description…"
          className={inputCls}
          autoComplete="off"
        />
      )}

      {/* Note */}
      <div>
        <label className="mb-1 block text-[12px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
          Note <span className="font-normal">(optional)</span>
        </label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          placeholder="Any context…"
          className={`${inputCls} resize-none`}
        />
      </div>

      {addMut.isError && (
        <p className="text-[12px] text-red-500 dark:text-red-400">Failed to save. Try again.</p>
      )}

      <button
        type="submit"
        disabled={!canSubmit || addMut.isPending}
        className="flex w-full items-center justify-center gap-2 rounded-lg bg-candy-pink px-4 py-2.5 text-[14px] font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-candy-pink-dark dark:text-candy-dark-bg"
      >
        {addMut.isPending && <Loader2 size={15} className="animate-spin" aria-hidden />}
        {addMut.isPending ? 'Saving…' : 'Log entry'}
      </button>
    </form>
  );
}

// --- Selected-slot panel ---

function SlotPanel({
  slot,
  date,
  entries,
  storeNo,
}: {
  slot: number;
  date: string;
  entries: SamplingEntry[];
  storeNo?: number;
}) {
  const [showForm, setShowForm] = useState(false);
  const formVisible = showForm || entries.length === 0;

  return (
    <div className="rounded-xl border border-candy-line bg-candy-white shadow-1 dark:border-candy-dark-line dark:bg-candy-dark-surface">
      <div className="flex items-center justify-between border-b border-candy-line px-4 py-3 dark:border-candy-dark-line">
        <span className="text-[15px] font-semibold text-candy-ink dark:text-candy-dark-ink">
          {formatSlotLong(slot)}
        </span>
        {entries.length > 0 && (
          <button
            type="button"
            onClick={() => setShowForm((p) => !p)}
            className="text-[13px] font-medium text-candy-pink hover:opacity-80 dark:text-candy-pink-dark"
          >
            {showForm ? 'Cancel' : '+ Add another'}
          </button>
        )}
      </div>

      {entries.length > 0 && (
        <div className="px-4">
          {entries.map((entry) => (
            <EntryRow key={entry.id} entry={entry} storeNo={storeNo} />
          ))}
        </div>
      )}

      {formVisible && (
        <div className="px-4 py-3">
          <AddForm
            slot={slot}
            date={date}
            storeNo={storeNo}
            onSuccess={() => setShowForm(false)}
          />
        </div>
      )}
    </div>
  );
}

// --- Page export ---

export function Sampling() {
  const activeStoreNo = useEffectiveStoreNo();

  const meQ = useStoreMe(activeStoreNo);
  const [date, setDate] = useState(() => todayInStoreZone(meQ.data?.store?.timezone));
  const [selectedSlot, setSelectedSlot] = useState<number | null>(null);

  const samplingQ = useSampling(date, activeStoreNo);

  const storeName = meQ.data?.store?.store_name;

  const entriesBySlot = (samplingQ.data?.entries ?? []).reduce<
    Record<number, SamplingEntry[]>
  >((acc, e) => {
    const slot = entrySlot(e);
    if (!acc[slot]) acc[slot] = [];
    acc[slot]!.push(e);
    return acc;
  }, {});

  const pageHead = (
    <div className="mb-5">
      <h1 className="text-2xl font-semibold text-candy-ink dark:text-candy-dark-ink">Sampling</h1>
      <p className="text-sm text-candy-ink-muted dark:text-candy-dark-ink-muted">
        Daily sampling log
        {storeName && (
          <>
            {' · '}
            <span className="font-medium">{storeName}</span>
          </>
        )}
      </p>
    </div>
  );

  if (meQ.isLoading || samplingQ.isLoading) {
    return (
      <div className="mx-auto w-full max-w-lg pb-24">
        {pageHead}
        <div className="space-y-4">
          {[0, 1].map((i) => (
            <div
              key={i}
              className="h-28 animate-pulse rounded-xl bg-candy-cream-warm dark:bg-candy-dark-surface-warm"
            />
          ))}
        </div>
      </div>
    );
  }

  if (samplingQ.error) {
    return (
      <div className="mx-auto w-full max-w-lg pb-24">
        {pageHead}
        <div className="rounded-xl border border-candy-line bg-candy-white px-4 py-6 text-center shadow-1 dark:border-candy-dark-line dark:bg-candy-dark-surface">
          <p className="text-sm text-candy-ink-muted dark:text-candy-dark-ink-muted">
            Could not load sampling data. Please try again.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-lg pb-24">
      {pageHead}

      {/* Date filter */}
      <div className="mb-4">
        <input
          type="date"
          value={date}
          onChange={(e) => {
            setDate(e.target.value);
            setSelectedSlot(null);
          }}
          className="rounded-md border border-candy-line bg-candy-white px-3 py-1.5 text-[13px] text-candy-ink focus:border-candy-pink focus:outline-none dark:border-candy-dark-line dark:bg-candy-dark-surface dark:text-candy-dark-ink dark:focus:border-candy-pink-dark"
        />
      </div>

      {/* Slot grid, half-hourly */}
      <div className="mb-4 grid grid-cols-4 gap-2">
        {SLOTS.map((slot) => (
          <SlotChip
            key={slot}
            slot={slot}
            entries={entriesBySlot[slot] ?? []}
            selected={selectedSlot === slot}
            onClick={() => setSelectedSlot((prev) => (prev === slot ? null : slot))}
          />
        ))}
      </div>

      {/* Selected slot panel */}
      {selectedSlot !== null && (
        <SlotPanel
          slot={selectedSlot}
          date={date}
          entries={entriesBySlot[selectedSlot] ?? []}
          storeNo={activeStoreNo}
        />
      )}

      {/* Empty state when nothing logged */}
      {selectedSlot === null && samplingQ.data?.entries.length === 0 && (
        <div className="rounded-xl border border-candy-line bg-candy-white px-4 py-6 text-center shadow-1 dark:border-candy-dark-line dark:bg-candy-dark-surface">
          <p className="text-[13px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
            No sampling entries yet. Tap a time to log your first item.
          </p>
        </div>
      )}
    </div>
  );
}
